import { managerRequestMapper } from '../mappers/group-admin-request.mapper';
import { packageMapper } from '../mappers/package.mapper';
import { Package } from '../models/package';
import { PackageType } from '../models/package-type';
import { User } from '../models/user';
import { appUrlsConfig } from './app-urls.config';
import { axiosService } from './axios.service';

/**
 * Get packages.
 * @param {object} data data
 * @param {PackageType[]} data.packageTypes Package type.
 * @param {User} data.groupAdmin Group admin.
 * @returns {Promise<Package[]>}
 */
async function getAssignablePackages({ packageTypes = [], groupAdmin }) {
  const params = {
    ...managerRequestMapper.toDto({ groupAdmin }),
    ...packageMapper.toQueryPackagesDto({ packageTypes }),
  };

  const results = await axiosService.get(
    appUrlsConfig.packages.getAssignablePackages,
    { params },
  );
  return results.data.map(p => packageMapper.fromDto(p));
}

/**
 * Get packages.
 * @param {object} data data
 * @param {PackageType[]} [data.packageTypes] Package type.
 * @param {boolean} [data.isBuyable] Is package buyable.
 * @param {number} [data.classCategoryId] Class category ID.
 * @param {string} [data.state] State.
 * @returns {Promise<Package[]>}
 */
async function getPackages(data) {
  const results = await axiosService.get(appUrlsConfig.packages.getPackagesV1, {
    params: packageMapper.toQueryPackagesDto({
      packageTypes: data?.packageTypes ?? [],
      isBuyable: data?.isBuyable,
      classCategoryId: data.classCategoryId,
      state: data.state,
    }),
  });
  return results.data.data.map(p => packageMapper.fromDto(p));
}

/**
 * Get package detail.
 * @param {number} id Package ID.
 * @returns {Promise<Package>}
 */
async function getPackageDetail(id) {
  const results = await axiosService.get(appUrlsConfig.packages.getPackageDetail(id));
  return packageMapper.fromDto(results.data);
}

/**
 * Get package category with items (v2 packages).
 * @param {string} state State.
 * @returns {Promise<Object<string,Package[]>>}
 */
async function getPackageCategoryWithItems(state) {
  const results = await axiosService.get(appUrlsConfig.packages.getPackages, {
    params: {
      state: state ?? undefined,
    },
  });
  return packageMapper.fromPackageCategoryWithItemsDto(results.data);
}

/**
 * Get admin addditional packages (v2 packages).
 * @returns {Promise<Object<string,Package[]>>}
 */
async function getAdminAdditionalPackages() {
  const results = await axiosService.get(appUrlsConfig.packages.getAdminPackages, {
    params: {
    },
  });
  return results.data;
}

/**
 * Delete available package.
 * @param {object} data data
 * @param {number} data.packageId Package ID.
 * @param {number} data.quantity Quantity to delete.
 * @param {number} [data.groupAdminId] Group admin ID.
 * @returns {Promise<void>}
 */
async function removePackageAvailableSeat({ packageId, quantity, groupAdminId }) {
  await axiosService.post(appUrlsConfig.packages.removePackageAvailableSeat(packageId), {
    quantity,
    group_admin_id: groupAdminId,
  });
}

export const packageService = {
  getAssignablePackages,
  getPackages,
  getPackageDetail,
  getPackageCategoryWithItems,
  getAdminAdditionalPackages,
  removePackageAvailableSeat,
};
